<!--

  Usage:

  <LineChart
    :series="[ { label: 'label', colour: 'red', points: { x0: y0, x1: y1, ... } }, ... } ]">
    <template slot="title">LineChart Title</template>
  </LineChart>

-->

<template>
  <div
    v-if="renderable"
    :class="{ scaled : this.height || this.width }"
    class="LineChart"
  >
    <svg ref="chart"></svg>
    <div ref="tooltip" class="Tooltip"></div>
  </div>

  <div
    v-else
    class="text-center red mt30 mb30"
  >
    Requires {{ minPoints }} data points to render graph.
  </div>
</template>

<script>
  import * as d3 from 'd3';
  import * as moment from 'moment';
  import { forEach } from '@/legacy/lib//util';
  import { rgba } from '@/legacy/lib//template';

  export default {
    props: {
      width: {
        type: Number
      },

      height: {
        type: Number
      },

      series: {
        type: Array
      },

      labels: {
        type: Object
      },

      curve: {
        type: String,
        default: 'curveLinear'
      },

      formatX: {
        type: Function,
        default: d => d
      },

      formatY: {
        type: Function,
        default: d => d
      },

      timescale: {
        type: Boolean,
        default: false
      },

      minPoints: {
        type: Number,
        default: 2
      },

      autoScale: {
        type: Boolean,
        default: true
      }
    },

    data () {
      return {
        renderable: true
      }
    },

    mounted () {
      let labels = this.labels;

      labels.x = 'x' in labels ? labels.x : '';
      labels.y = 'y' in labels ? labels.y : '';
      labels.uX = 'uX' in labels ? labels.uX : labels.x;
      labels.uY = 'uY' in labels ? labels.uY : labels.y;

      let svg     = d3.select (this.$refs.chart);
      let tooltip = d3.select (this.$refs.tooltip);

      let margin = {
        top:    30,
        right:  30,
        bottom: 60,
        left:   80
      };

      /** **/

      let renderable = false;

      for (let series of this.series) {
        if (series.points.length >= this.minPoints) {
          renderable = true;
          break;
        }
      }

      this.renderable = renderable;

      if (!this.renderable) {
        return;
      }

      /** **/

      let boundingBox = svg
        .node ()
        .getBoundingClientRect ();

      // let width  = boundingBox.width - margin.left - margin.right;
      // // let height = boundingBox.height - margin.top - margin.bottom;
      // let height = 300 + margin.top + margin.bottom;

      let width  = boundingBox.width - margin.left - margin.right;
      let height = boundingBox.height - margin.top - margin.bottom;

      if (this.width) {
        width = this.width;
      }

      if (this.height) {
        height = this.height;
      }

      let formatX = this.formatX;

      if (this.timescale) {
        formatX = d => moment (d * 1000).format ('DD/MM/YY');
      }

      let g = svg
        .attr ('width', width + margin.left + margin.right)
        .attr ('height', height + margin.top + margin.bottom)
        .append ('g')
        .attr ('transform', `translate(${margin.left}, ${margin.top})`);

      let x = d3
        .scaleLinear ()
        .range ([ 0, width ])
        .domain ([ this.minX, this.maxX ]);

      let y = d3
        .scaleLinear ()
        .range ([ height, 0 ])
        .domain ([ this.autoScale ? Math.max (this.minY - 100, 0) : 0, this.autoScale ? this.maxY + 100 : this.maxY ])
        .nice ();

      let line = d3
        .line ()
        .curve (d3 [this.curve])
        .x (d => { return x (d.x); })
        .y (d => { return y (d.y); });

      svg
        .append ('g')
        .attr ('class', 'x axis-grid')
        .attr ('transform', `translate(${margin.left}, ${height + margin.top})`)
        .call (
          d3
            .axisBottom (x)
            .tickSize (-height)
            .tickFormat ('')
            .ticks (8)
        );

      svg
        .append ('g')
        .attr ('class', 'y axis-grid')
        .attr ('transform', `translate(${margin.left}, ${margin.top})`)
        .call (
          d3
            .axisLeft (y)
            .tickSize (-width)
            .tickFormat ('')
            .ticks (8)
        );

      g
        .append ('g')
        .attr ('class', 'axis axis--x')
        .attr ('transform', `translate(0, ${height})`)
        .call (
          d3
            .axisBottom (x)
            .tickFormat (formatX)
        );

      svg
        .append ('text')
        .attr ('transform', `translate(${width / 2 + margin.left}, ${height + margin.top + 50})`)
        .style ('text-anchor', 'middle')
        .attr ('class', 'Label')
        .text (labels.x);

      g
        .append ('g')
        .attr ('class', 'axis axis--y')
        .call (
          d3
            .axisLeft (y)
            .tickFormat (this.formatY)
        )
        .append ('text')
        .attr ('transform', 'rotate(-90)')
        .attr ('y', -margin.left + 20)
        .attr ('x', -height / 2)
        .attr ('dy', '1em')
        .attr ('class', 'Label')
        .style ('text-anchor', 'middle')
        .text (labels.y);

      let series = g
        .selectAll ('.series')
        .data (this.series)
        .enter ()
        .append ('g')
        .attr ('class', 'series');

      series
        .append ('path')
        .attr ('class', 'line')
        .attr ('stroke', d => rgba (`--wc3-${d.colour}`))
        .attr ('d', d => { return line (d.points); })
        .style ('fill', 'transparent');

      /** **/

      let area = d3
        .area ()
        .x (d => x (d.x))
        .y0 (height)
        .y1 (d => y (d.y));

      let areas = g
        .selectAll ('.areas')
        .data (this.series)
        .enter ()
        .append ('g')
        .attr ('class', 'areas');

      areas
        .append ('path')
        .attr ('fill', d => rgba(`--wc3-${d.colour}`, 0.04))
        .attr ('class', 'area')
        .attr ('d', d => area (d.points));

      /** **/

      // let legend = svg
      //   .selectAll ('.legend')
      //   .data (this.series)
      //   .enter ()
      //   .append ('g')
      //   .attr ('class', 'legend');

      // legend
      //   .append ('rect')
      //   .attr ('x', width - 65)
      //   .attr ('y', (d, i) => i * 20 + 2)
      //   .attr ('width', 10)
      //   .attr ('height', 10)
      //   .style ('fill', d => rgba (`--wc3-${d.colour}`));

      // legend
      //   .append ('text')
      //   .attr ('x', width - 45)
      //   .attr ('y', (d, i) => i * 20 + 15)
      //   .text (d => d.label);


      /** **/

      let tooltipLine = svg
        .append ('line')
        .style ('stroke', rgba('--white'));

      let tipBox = svg
        .append ('rect')
        .attr ('width', width)
        .attr ('height', height)
        .attr ('opacity', 0)
        .attr ('x', margin.left)
        .attr ('y', margin.top);

      tipBox.on ('mousemove', () => {
        let dx = d3.mouse (tipBox.node ()) [0];
        let dy = d3.mouse (tipBox.node ()) [1];

        let inverse = x.invert (dx - margin.left);
        let bisect  = d3.bisector (d => d.x).left;
        // let points  = this.series [bisect (this.series, inverse)];

        let content = ``;

        content += `${labels.x}: ${formatX (Math.floor (inverse))} ${labels.uX} <br>`;

        forEach (this.series, (series) => {
          // if (inverse > series.points.length) {
          //   return;
          // }

          let d = series.points [bisect (series.points, inverse)];

          // console.log (d.y);
          // console.log (inverse);
          // console.log (series.points);
          // console.log (bisect (series.points, inverse));

          if (!d) {
            return;
          }

          content += `<div style="color: ${rgba (`--wc3-${series.colour}`)}">`;
          content += `${series.label}: ${d.y} ${labels.uY}`;
          content += `</div>`;
        });

        tooltipLine
          .attr ('stroke', rgba ('--black'))
          .attr ('display', 'block')
          .attr ('x1', dx)
          .attr ('x2', dx)
          .attr ('y1', margin.top)
          .attr ('y2', height + margin.top);

        tooltip.html (content)
          .style ('left', `${dx + 20}px`)
          .style ('top', `${dy - 20}px`)
          .style ('display', 'block');

        // content = `Game Time: ${Math.floor (inverse)} min elapsed <br />`,
        // keys  = Object.keys (points),
        // index   = keys.indexOf ('tick');

        // if (index !== -1) {
        // keys.splice (index, 1);
        // }

        // keys.sort ((x, y) => {
        // return points [y] - points [x];
        // });

        // for (let name of keys) {
        // if (name === 'tick') {
        // continue;
        // }


        // content += `<div class="WC--text-${name.split (':') [0].toLowerCase ()}">
        // ${name.split (':') [1]} - ${points [name]}
        // </div>`;
        // }
      })

      tipBox.on ('mouseout', () => {
        tooltip.style ('display', 'none');
        tooltipLine.attr ('display', 'none');
      });
    },

    computed: {
      minX () {
        let minX = Infinity;

        forEach (this.series, (series) => {
          minX = Math.min (
            minX,
            d3.min (series.points, d => d.x)
          );
        });

        return minX;
      },

      maxX () {
        let maxX = null;

        forEach (this.series, (series) => {
          maxX = Math.max (
            maxX,
            d3.max (series.points, d => d.x)
          );
        });

        return maxX;
      },

      maxY () {
        let maxY = null;

        forEach (this.series, (series) => {
          maxY = Math.max (
            maxY,
            d3.max (series.points, d => d.y)
          );
        });

        return maxY;
      },

      minY () {
        let minY = Infinity;

        forEach (this.series, (series) => {
          minY = Math.min (
            minY,
            d3.min (series.points, d => d.y)
          );
        });

        return minY;
      }
    }
  };
</script>

<style lang="scss">
  .LineChart {
    position: relative;
    height: 100%;

    svg {
      width: 100%;
      height: 100%;
    }

    text {
      fill: rgba(var(--white), 0.8);
      // font-size: fetch(font m);
      font-family: fetch(font family);
    }
  }

  .LineChart.scaled {
    height: auto !important;
  }

  .axis-grid {
    line {
      stroke: rgba(var(--white), .1);
    }

    path {
      stroke: none;
    }
  }
</style>